// 반복(repeat) 위젯 속성 패널의 over(반복 대상) 후보 산출 — 순수 함수.
//
// 중첩 repeat(반복 안 반복, P4)이면 부모 repeat 소스의 **행 배열 필드로 선언된 이름과 같은 key의
// dataSource**(자식 소스 — P3 표준: editMode:'readonly' + allowAddRemove:false + key=부모 행
// 배열 필드명)를 후보에 추가 노출한다. 엔진은 안쪽 rows를 values[over]=부모 행 객체의 그 필드에서
// 읽으므로(RepeatRowsBlock 행 스코프 재귀), "부모 fields 선언 ∩ dataSources key"가 곧 유효 후보다
// (선언=계약 — 미선언 key는 안내 폴백). 최상위(비중첩) repeat은 현행(편집형 list)만 유지.
// 결정: docs/decisions/2026-08-17-composer-nested-repeat-photo-group.md

import type { DataSourceDef } from '@safetyhub/shared';
import { editableListDataSources } from '@safetyhub/report-composer';

export interface RepeatOverCandidates {
  /**
   * 중첩 전용 자식 소스 후보 — 부모 소스 def.fields의 field명과 같은 key의 list dataSource
   * (readonly 포함, hidden 필드도 인정 — 자식 배열 필드는 행 그리드 표시 대상이 아니라 hidden
   * 선언이 자연스럽다). 비중첩(부모 없음/부모 over 미해석)이면 빈 배열.
   */
  parentRowFields: DataSourceDef[];
  /**
   * 현행 후보(편집형 list, 선언 순서 유지). 중첩 컨텍스트에서 key가 부모 행 필드와 겹치면
   * parentRowFields 쪽으로만 노출한다(중복 option 방지 — 엔진이 그 key를 부모 행 필드로
   * 읽으므로 그 그룹이 실제 동작을 말한다).
   */
  editable: DataSourceDef[];
}

/**
 * repeat 위젯의 over select 후보. parentOver=이 repeat을 감싼 부모 repeat의 over
 * (findEnclosingRepeatWidget 결과 — 비중첩이면 undefined).
 */
export function repeatOverCandidates(
  dataSources: DataSourceDef[] | undefined,
  parentOver: string | undefined,
): RepeatOverCandidates {
  const editable = editableListDataSources(dataSources);
  const parentDef = parentOver ? (dataSources ?? []).find((d) => d.key === parentOver) : undefined;
  if (!parentDef) return { parentRowFields: [], editable };
  const rowFieldNames = new Set(parentDef.fields.map((f) => f.field));
  const parentRowFields = (dataSources ?? []).filter(
    (d) => d.cardinality === 'list' && rowFieldNames.has(d.key),
  );
  const nestedKeys = new Set(parentRowFields.map((d) => d.key));
  return { parentRowFields, editable: editable.filter((d) => !nestedKeys.has(d.key)) };
}
